import { useGetBookmarkStatus, useToggleBookmark } from '@/api/bookmarks/bookmarks';
import { getGetBookmarkStatusQueryKey } from '@/api/bookmarks/bookmarks';
import { useGetMyCurrentBalance } from '@/api/balance/balance';
import type { VoucherResponse } from '@/api/model/voucherResponse';
import { VoucherStatus } from '@/api/model/voucherStatus';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { DateTimeDisplay } from '@/components/ui/datetime-display';
import { useAuth } from '@/contexts/AuthContext';
import { cn, isDateBetween } from '@/lib/utils';
import { getApiErrorMessage } from '@/lib/api-error';
import { useQueryClient } from '@tanstack/react-query';
import { Bookmark, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { VoucherCategoryIcon } from './VoucherCategoryIcon';

interface VoucherCardProps {
  voucher: VoucherResponse;
  onClick?: () => void;
  hideBookmark?: boolean;
  pdfUrl?: string | null;
  isPreview?: boolean;
}

export const VoucherCard: React.FC<VoucherCardProps> = ({
  voucher,
  onClick,
  hideBookmark = false,
  pdfUrl,
  isPreview = false,
}) => {
  const isAvailable = checkVoucherAvailability(voucher);
  // For now, we'll consider all vouchers as potentially recommendable
  // This can be updated based on future business logic
  const isRecommended = false;

  const { isAuthenticated } = useAuth();
  const { data: balance } = useGetMyCurrentBalance({
    query: {
      enabled: isAuthenticated && !isPreview,
    },
  });

  // Check if user has insufficient balance for this voucher
  const hasInsufficientBalance = useMemo(() => {
    if (!isAuthenticated || !balance || isPreview) {
      return false;
    }
    const voucherValue = typeof voucher.value === 'number' ? voucher.value : Number(voucher.value);
    return Number.isFinite(voucherValue) && balance.remaining_credit < voucherValue;
  }, [isAuthenticated, balance, voucher.value, isPreview]);

  // Combine availability and balance checks for visual styling
  const isDisabled = !isAvailable || hasInsufficientBalance;

  const queryClient = useQueryClient();

  // Get bookmark status (only if bookmark feature is enabled)
  const { data: bookmarkStatus } = useGetBookmarkStatus(voucher.id, {
    query: {
      enabled: isAvailable && !hideBookmark && !isPreview,
    },
  });

  const isBookmarked = bookmarkStatus?.is_bookmarked ?? false;
  const [previewBookmarkState, setPreviewBookmarkState] = useState(false);

  useEffect(() => {
    if (isPreview) {
      setPreviewBookmarkState(false);
    }
  }, [isPreview, voucher.id]);
  const currentBookmarkState = isPreview ? previewBookmarkState : isBookmarked;

  const toggleBookmarkMutation = useToggleBookmark({
    mutation: {
      onSuccess: data => {
        // Update only the specific voucher's bookmark status in the cache
        queryClient.setQueryData(getGetBookmarkStatusQueryKey(voucher.id), data);
        // Invalidate the bookmarked vouchers list to refresh the list view
        queryClient.invalidateQueries({
          queryKey: ['/api/bookmarks/vouchers'],
        });
      },
    },
  });

  const handleBookmarkClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isPreview) {
      setPreviewBookmarkState(prev => !prev);
      return;
    }

    if (isAvailable && !hideBookmark) {
      try {
        // Only toggle bookmark for this specific voucher
        await toggleBookmarkMutation.mutateAsync({ voucherId: voucher.id });
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Fehler beim Aktualisieren der Merkliste'));
      }
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on interactive elements (buttons, links)
    // but allow clicks on the card itself
    const target = e.target as HTMLElement;
    const cardElement = e.currentTarget as HTMLElement;

    // Check if we clicked on a button or link that's a child of the card
    const clickedButton = target.closest('button');
    const clickedLink = target.closest('a');

    // Only prevent card click if we clicked on an actual button/link element
    // (not the card itself which also has role="button")
    if (clickedButton && clickedButton !== cardElement) {
      return;
    }
    if (clickedLink && clickedLink !== cardElement) {
      return;
    }

    if (isAvailable && !hasInsufficientBalance && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isAvailable || hasInsufficientBalance) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (onClick) {
        onClick();
      }
    }
  };

  const formatDiscount = () => {
    const numericValue = typeof voucher.value === 'number' ? voucher.value : Number(voucher.value);

    if (Number.isFinite(numericValue)) {
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
      }).format(numericValue);
    }

    // Sensible fallback when value is missing/invalid
    return '';
  };

  const getCategoryName = () => {
    return voucher.provider.category.toUpperCase();
  };

  const images = useMemo(() => {
    if (!Array.isArray(voucher.images)) {
      return [];
    }

    return voucher.images
      .map(image => {
        if (typeof image === 'string') {
          return image.trim();
        }
        if (image == null) {
          return '';
        }
        return String(image).trim();
      })
      .filter(url => url.length > 0)
      .slice(0, 5);
  }, [voucher.images]);
  const imagesKey = useMemo(() => images.join('|'), [images]);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  useEffect(() => setActiveImageIndex(0), [voucher.id, imagesKey]);

  const activeImage = images.length > 0 ? images[Math.min(activeImageIndex, images.length - 1)] : null;

  return (
    <Card
      role='button'
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      className={cn(
        'group relative overflow-hidden transition-all duration-200 hover:shadow-lg rounded-xl',
        'bg-neutral-weiss border border-swd-tuerkis-light',
        isDisabled ? 'opacity-60 grayscale cursor-not-allowed' : 'cursor-pointer hover:shadow-xl'
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Image Section */}
      <div className='relative h-56 -top-6 w-full overflow-hidden bg-swd-tuerkis/10'>
        <img src={activeImage || '/placeholder.png'} alt={voucher.title} className='h-full w-full object-cover' />
        {/* Overlay with tags and category button */}
        <div className='absolute inset-0 pointer-events-none'>
          <div className='flex items-center justify-between px-[21.34px] pt-[13px]'>
            {/* Recommended tag */}
            {isRecommended && (
              <Badge className='bg-neutral-weiss text-swd-tuerkis-darker text-[11px] font-semibold leading-4 tracking-[0.33px] px-2 py-0.5 rounded-xl'>
                Empfohlener Deal!
              </Badge>
            )}
            {/* Category button */}
            <div className='absolute top-3 right-3 z-10 pointer-events-auto grayscale-0'>
              <Button
                variant='secondary'
                size='icon'
                className='bg-neutral-weiss text-swd-tuerkis rounded-full w-9 h-9 p-0 shadow-[0px_2px_4px_0px_rgba(0,0,0,0.1),0px_4px_6px_0px_rgba(0,0,0,0.1)] hover:bg-neutral-weiss disabled:!bg-neutral-weiss disabled:opacity-100 [&.btn-disabled]:!bg-neutral-weiss'
                onClick={e => {
                  e.stopPropagation();
                }}
                disabled={!isAvailable}
              >
                <VoucherCategoryIcon category={voucher.provider.category} />
              </Button>
            </div>
          </div>
        </div>
        {images.length > 1 && (
          <div className='absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/25 px-2 py-1 backdrop-blur-sm'>
            {images.length > 1 && (
              <Button
                variant='ghost'
                size='icon'
                className='h-6 w-6 rounded-full bg-white/20 text-white hover:bg-white/40'
                onClick={event => {
                  event.stopPropagation();
                  setActiveImageIndex((prevIndex: number) => (prevIndex - 1 + images.length) % images.length);
                }}
                aria-label='Vorheriges Bild'
              >
                <ChevronLeft className='size-4' />
              </Button>
            )}
            <div className='flex items-center gap-2 px-1'>
              {images.map((_, index) => (
                <Button
                  key={`${voucher.id}-dot-${index}`}
                  variant='ghost'
                  size='icon'
                  className={cn(
                    'h-2.5 w-2.5 rounded-full border border-white/40 bg-white/30 p-0 hover:bg-white/70 focus-visible:ring-2 focus-visible:ring-white/60',
                    index === activeImageIndex && 'bg-white border-white shadow-[0_0_0_2px_rgba(0,0,0,0.1)]'
                  )}
                  onClick={event => {
                    event.stopPropagation();
                    setActiveImageIndex(index);
                  }}
                  aria-label={`Bild ${index + 1} anzeigen`}
                />
              ))}
            </div>
            {images.length > 1 && (
              <Button
                variant='ghost'
                size='icon'
                className='h-6 w-6 rounded-full bg-white/20 text-white hover:bg-white/40'
                onClick={event => {
                  event.stopPropagation();
                  setActiveImageIndex((prevIndex: number) => (prevIndex + 1) % images.length);
                }}
                aria-label='Nächstes Bild'
              >
                <ChevronRight className='size-4' />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Content Section */}
      <CardContent className='px-5 py-0 bg-white/20 flex-grow flex flex-col min-h-0'>
        <div className='flex flex-col gap-4 flex-1 min-h-0'>
          <div className='flex flex-col gap-2'>
            {/* Header with category and discount */}
            <div className='flex items-center justify-between h-9'>
              <span
                className={cn(
                  'text-base font-medium leading-6 tracking-[0.64px] font-outfit',
                  isDisabled ? 'text-muted-foreground' : 'text-swd-tuerkis'
                )}
              >
                {getCategoryName()}
              </span>
              <span className='text-swd-tuerkis-darker text-[28px] font-semibold leading-9 font-outfit text-right'>
                {formatDiscount()}
              </span>
            </div>

            {/* Title & provider */}
            <div className='flex flex-col gap-1'>
              <h3 className='text-xl font-semibold leading-[26px] text-neutral-dunkelgrau font-outfit'>
                {voucher.title}
              </h3>
              <span className='text-sm font-medium leading-5 text-neutral-grau font-outfit'>
                Angebot von <span className='text-swd-tuerkis'>{voucher.provider.name}</span>
              </span>
            </div>
          </div>

          {/* Description */}
          {voucher.description && (
            <p className='text-neutral-grau text-sm font-medium leading-5 tracking-[0.56px] font-outfit'>
              {voucher.description}
            </p>
          )}

          {/* Validity badge */}
          <div className='inline-flex items-center bg-neutral-weiss rounded-xl px-2 py-0.5 w-auto max-w-max'>
            <span className='text-swd-tuerkis-darker text-xs font-semibold leading-4 tracking-wide font-outfit'>
              Gültig bis: <DateTimeDisplay date={voucher.valid_until} format='date-only' />
            </span>
          </div>
        </div>

        {/* Footer with action - border-t with proper spacing */}
        <div className='mt-auto pt-4 border-t-[0.5px] border-swd-tuerkis-light'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <span
                className={cn(
                  'text-sm font-medium leading-5 tracking-[0.56px] font-outfit',
                  isDisabled ? 'text-muted-foreground' : 'text-swd-tuerkis-darker'
                )}
              >
                Details ansehen
              </span>
            </div>
            <div className='flex items-center gap-2'>
              {pdfUrl && (
                <Button
                  asChild
                  variant='outline'
                  size='sm'
                  className='flex items-center gap-2'
                  onClick={e => e.stopPropagation()}
                >
                  <a href={pdfUrl} target='_blank' rel='noopener noreferrer' onClick={e => e.stopPropagation()}>
                    <Download className='w-4 h-4' />
                    PDF
                  </a>
                </Button>
              )}
              {!hideBookmark && (
                <Button
                  variant='ghost'
                  size='icon'
                  className='p-0 size-6 bg-neutral-weiss'
                  onClick={handleBookmarkClick}
                  aria-label={currentBookmarkState ? 'Von Merkliste entfernen' : 'Merken'}
                  disabled={!isPreview && (isDisabled || toggleBookmarkMutation.isPending)}
                >
                  <Bookmark
                    className={cn(
                      'size-6 transition-colors',
                      !isPreview && toggleBookmarkMutation.isPending ? 'text-muted-foreground' : 'text-swd-orange'
                    )}
                    fill={currentBookmarkState ? 'currentColor' : 'none'}
                  />
                </Button>
              )}
            </div>
          </div>
        </div>

        {isDisabled && (
          <div className='text-xs text-destructive font-medium mt-2'>
            {hasInsufficientBalance ? 'Unzureichendes Guthaben' : getUnavailabilityReason(voucher)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

function checkVoucherAvailability(voucher: VoucherResponse): boolean {
  if (!voucher.is_active) {
    return false;
  }

  if (voucher.status !== VoucherStatus.PUBLISHED) {
    return false;
  }

  // Use timezone-aware date comparison
  if (!isDateBetween(voucher.valid_from, voucher.valid_until)) {
    return false;
  }

  if (voucher.max_redemptions && voucher.current_redemptions >= voucher.max_redemptions) {
    return false;
  }

  return true;
}

function getUnavailabilityReason(voucher: VoucherResponse): string {
  if (!voucher.is_active) {
    return 'Nicht aktiviert';
  }
  if (voucher.status !== VoucherStatus.PUBLISHED) {
    return 'Nicht veröffentlicht';
  }
  if (!isDateBetween(voucher.valid_from, voucher.valid_until)) {
    return 'Nicht im gültigen Zeitraum';
  }
  if (voucher.max_redemptions && voucher.current_redemptions >= voucher.max_redemptions) {
    return 'Maximale Einlösungen erreicht';
  }
  return 'Nicht verfügbar';
}
